import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, Download, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { useLang } from "../i18n";
import { CheckGroup, DynRows, Field, YesNo } from "../components/fields";
import { AttachmentInput, PublicLinkButton } from "../components/FormLink";
import CountrySelect from "../components/CountrySelect";
import SignaturePad from "../components/SignaturePad";
import FormRecordsList from "../components/FormRecordsList";
import { Plus } from "lucide-react";
import DateInput from "../components/DateInput";

const initial = {
  record_type: "nuevo", first_name: "", second_name: "", last_name_p: "", last_name_m: "", last_name_c: "",
  id_number: "", birth_date: "", gender: "", nationality: "", residence_country: "", marital_status: "",
  birth_country: "", home_phone: "", mobile: "", email: "", po_box: "", address: "",
  employer: "", profession: "", work_phone: "", position: "",
  income_main: "", income_other: "", payment_methods: [], payment_other: "", funds_origin: "",
  is_pep: null, pep_position: "", pep_date: "", is_pep_relative: null, pep_relative_name: "",
  bank_refs: [], commercial_refs: [],
  dd_process: null, dd_process_desc: "", investigated: null, investigated_desc: "",
  client_name: "", date: new Date().toISOString().slice(0, 10), signature: null,
};

export default function FormNatural({ publicToken = null }) {
  const { t } = useLang();
  const locationEdit = useLocation().state?.edit || null;
  const [editDoc, setEditDoc] = useState(locationEdit);
  const [showForm, setShowForm] = useState(!!locationEdit);
  const [d, setD] = useState(() => (locationEdit ? { ...initial, ...locationEdit.data } : initial));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [sec, setSec] = useState(0);
  const [atts, setAtts] = useState([]);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setD({ ...d, [k]: e?.target ? e.target.value : e });
  const inp = (key, testid, type = "text") => (
    type === "date"
      ? <DateInput value={d[key]} onChange={set(key)} data-testid={testid} />
      : <input className="ec-input" type={type} value={d[key]} onChange={set(key)} data-testid={testid} />
  );

  const scrollToForm = () => setTimeout(() => document.querySelector('[data-testid="pn-form-wrap"]')?.scrollIntoView({ behavior: "smooth" }), 80);

  const newForm = () => {
    setEditDoc(null);
    setD(initial);
    setSaved(null);
    setSec(0);
    setShowForm(true);
    scrollToForm();
  };

  const startEdit = (doc) => {
    setEditDoc(doc);
    setD({ ...initial, ...doc.data });
    setSaved(null);
    setSec(0);
    setShowForm(true);
    scrollToForm();
  };

  const sections = [
    {
      title: t("n.sec1"), testid: "sec-personal",
      content: (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label={t("n.record_type")}>
            <select className="ec-input" value={d.record_type} onChange={set("record_type")} data-testid="pn-record-type">
              <option value="nuevo">{t("n.rt_new")}</option>
              <option value="actualizacion">{t("n.rt_update")}</option>
            </select>
          </Field>
          <Field label={t("n.first_name")}>{inp("first_name", "pn-first-name")}</Field>
          <Field label={t("n.second_name")}>{inp("second_name", "pn-second-name")}</Field>
          <Field label={t("n.last_name_p")}>{inp("last_name_p", "pn-last-name-p")}</Field>
          <Field label={t("n.last_name_m")}>{inp("last_name_m", "pn-last-name-m")}</Field>
          <Field label={t("n.last_name_c")}>{inp("last_name_c", "pn-last-name-c")}</Field>
          <Field label={t("n.id_number")}>{inp("id_number", "pn-id-number")}</Field>
          <Field label={t("n.birth_date")}>{inp("birth_date", "pn-birth-date", "date")}</Field>
          <Field label={t("n.gender")}>
            <select className="ec-input" value={d.gender} onChange={set("gender")} data-testid="pn-gender">
              <option value="">—</option>
              <option>{t("n.gender_m")}</option><option>{t("n.gender_f")}</option><option>{t("n.gender_o")}</option>
            </select>
          </Field>
          <Field label={t("n.nationality")}><CountrySelect value={d.nationality} onChange={(e) => setD((p) => ({ ...p, nationality: e.target.value }))} testid="pn-nationality" /></Field>
          <Field label={t("n.residence_country")}><CountrySelect value={d.residence_country} onChange={(e) => setD((p) => ({ ...p, residence_country: e.target.value }))} testid="pn-residence-country" /></Field>
          <Field label={t("n.marital_status")}>
            <select className="ec-input" value={d.marital_status} onChange={set("marital_status")} data-testid="pn-marital-status">
              <option value="">—</option>
              <option>{t("n.ms_single")}</option><option>{t("n.ms_married")}</option>
              <option>{t("n.ms_divorced")}</option><option>{t("n.ms_widowed")}</option>
            </select>
          </Field>
          <Field label={t("n.birth_country")}><CountrySelect value={d.birth_country} onChange={(e) => setD((p) => ({ ...p, birth_country: e.target.value }))} testid="pn-birth-country" /></Field>
          <Field label={t("n.home_phone")}>{inp("home_phone", "pn-home-phone")}</Field>
          <Field label={t("n.mobile")}>{inp("mobile", "pn-mobile")}</Field>
          <Field label={t("n.email")}>{inp("email", "pn-email", "email")}</Field>
          <Field label={t("n.po_box")}>{inp("po_box", "pn-po-box")}</Field>
          <Field label={t("n.address")} className="sm:col-span-2 lg:col-span-3">
            <textarea className="ec-input" rows={2} value={d.address} onChange={set("address")} data-testid="pn-address" />
          </Field>
          <Field label={t("n.employer")}>{inp("employer", "pn-employer")}</Field>
          <Field label={t("n.profession")}>{inp("profession", "pn-profession")}</Field>
          <Field label={t("n.work_phone")}>{inp("work_phone", "pn-work-phone")}</Field>
          <Field label={t("n.position")}>{inp("position", "pn-position")}</Field>
        </div>
      ),
    },
    {
      title: t("n.sec2"), testid: "sec-financial",
      content: (
        <div className="space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label={t("n.income_main")}>
              <select className="ec-input" value={d.income_main} onChange={set("income_main")} data-testid="pn-income-main">
                <option value="">—</option>
                {[t("n.r1"), t("n.r2"), t("n.r3"), t("n.r4")].map((r) => <option key={r}>{r}</option>)}
              </select>
            </Field>
            <Field label={t("n.income_other")}>
              <select className="ec-input" value={d.income_other} onChange={set("income_other")} data-testid="pn-income-other">
                <option value="">—</option>
                {[t("n.r1"), t("n.r2"), t("n.r3"), t("n.r4")].map((r) => <option key={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t("n.payment_methods")}>
            <CheckGroup
              options={[t("n.pm_check"), t("n.pm_cc"), t("n.pm_wire"), t("n.pm_ach"), t("n.pm_cash"), t("n.pm_other")]}
              values={d.payment_methods}
              onChange={(v) => setD({ ...d, payment_methods: v })}
              testidPrefix="pn-pm"
            />
          </Field>
          <Field label={t("n.payment_other")}>{inp("payment_other", "pn-payment-other")}</Field>
          <Field label={t("n.funds_origin")}>
            <textarea className="ec-input" rows={3} value={d.funds_origin} onChange={set("funds_origin")} data-testid="pn-funds-origin" />
          </Field>
        </div>
      ),
    },
    {
      title: t("n.sec3"), testid: "sec-pep",
      content: (
        <div className="space-y-5">
          <Field label={t("n.is_pep")}><YesNo value={d.is_pep} onChange={(v) => setD({ ...d, is_pep: v })} testid="pn-is-pep" /></Field>
          {d.is_pep && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("n.pep_position")}>{inp("pep_position", "pn-pep-position")}</Field>
              <Field label={t("n.pep_date")}>{inp("pep_date", "pn-pep-date", "date")}</Field>
            </div>
          )}
          <Field label={t("n.is_pep_relative")}><YesNo value={d.is_pep_relative} onChange={(v) => setD({ ...d, is_pep_relative: v })} testid="pn-pep-relative" /></Field>
          {d.is_pep_relative && <Field label={t("n.pep_relative_name")}>{inp("pep_relative_name", "pn-pep-relative-name")}</Field>}
        </div>
      ),
    },
    {
      title: t("n.sec4"), testid: "sec-bank-refs",
      content: <DynRows testid="pn-bank-refs" columns={[{ key: "bank", label: t("n.bank") }, { key: "contact", label: t("n.contact") }, { key: "phone", label: t("n.phone") }]} rows={d.bank_refs} onChange={(v) => setD({ ...d, bank_refs: v })} />,
    },
    {
      title: t("n.sec5"), testid: "sec-commercial-refs",
      content: <DynRows testid="pn-commercial-refs" columns={[{ key: "name", label: t("n.legal_name_ref") }, { key: "contact", label: t("n.contact") }, { key: "phone", label: t("n.phone") }]} rows={d.commercial_refs} onChange={(v) => setD({ ...d, commercial_refs: v })} />,
    },
    {
      title: t("n.sec6"), testid: "sec-questions",
      content: (
        <div className="space-y-5">
          <Field label={t("n.dd_process")}><YesNo value={d.dd_process} onChange={(v) => setD({ ...d, dd_process: v })} testid="pn-dd-process" /></Field>
          {d.dd_process && <Field label={t("n.dd_process_desc")}><textarea className="ec-input" rows={2} value={d.dd_process_desc} onChange={set("dd_process_desc")} data-testid="pn-dd-process-desc" /></Field>}
          <Field label={t("n.investigated")}><YesNo value={d.investigated} onChange={(v) => setD({ ...d, investigated: v })} testid="pn-investigated" /></Field>
          {d.investigated && <Field label={t("n.investigated_desc")}><textarea className="ec-input" rows={2} value={d.investigated_desc} onChange={set("investigated_desc")} data-testid="pn-investigated-desc" /></Field>}
        </div>
      ),
    },
    {
      title: t("n.sec7"), testid: "sec-signature",
      content: (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-4">
            <Field label={t("n.client_name")}>{inp("client_name", "pn-client-name")}</Field>
            <Field label={t("common.date")}>{inp("date", "pn-sign-date", "date")}</Field>
            <AttachmentInput value={atts} onChange={setAtts} testid="pn-attachments" />
          </div>
          <Field label={t("forms.signature")}>
            <SignaturePad onChange={(v) => setD((p) => ({ ...p, signature: v }))} />
          </Field>
        </div>
      ),
    },
  ];

  const submit = async () => {
    if (!d.signature) {
      toast.error(t("forms.sign_required"));
      setSec(6);
      return;
    }
    setBusy(true);
    try {
      if (publicToken) {
        const { data } = await api.post(`/public/form/${publicToken}`, { data: d, attachments: atts });
        setDone(data.form_number);
      } else if (editDoc) {
        await api.patch(`/forms/${editDoc.id}`, { data: d });
        setSaved({ id: editDoc.id, form_number: editDoc.form_number });
        toast.success(t("forms.updated"));
      } else {
        const { data } = await api.post("/forms", { form_type: "dd_persona_natural", data: d, attachments: atts });
        setSaved(data);
        toast.success(`${t("forms.save_success")} — ${data.form_number}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="ec-card p-10 text-center space-y-3 max-w-xl mx-auto" data-testid="form-public-done">
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
        <h2 className="font-heading text-xl font-extrabold text-slate-900">{t("forms.public_done")}</h2>
        <p className="text-sm text-slate-600">{t("forms.public_done_no")} <span className="font-mono font-bold">{done}</span></p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="form-natural-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("n.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("n.subtitle")}</p>
        </div>
        {!publicToken && (
          <div className="flex items-center gap-2">
            <button onClick={newForm} data-testid="pn-create-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
              <Plus className="w-4 h-4" /> {t("frl.new")}
            </button>
            <PublicLinkButton formType="dd_persona_natural" testid="pn-public-link" />
          </div>
        )}
      </div>

      {!publicToken && <FormRecordsList formType="dd_persona_natural" onEdit={startEdit} refresh={saved} testid="pn" />}

      {saved && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3" data-testid="form-saved-banner">
          <span className="text-sm text-emerald-800 font-semibold">{t("forms.doc_no")} <span className="font-mono">{saved.form_number}</span></span>
          <button onClick={() => downloadFile(`/forms/${saved.id}/pdf`, `${saved.form_number}.pdf`)} data-testid="download-form-pdf" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C]">
            <Download className="w-3.5 h-3.5" /> {t("common.download_pdf")}
          </button>
          <button onClick={() => { setD(initial); setEditDoc(null); setSaved(null); setSec(0); }} data-testid="new-form-button" className="text-xs font-bold text-slate-600 hover:text-slate-900 underline">
            {t("n.rt_new")}
          </button>
        </div>
      )}

      {(publicToken || showForm) && (
        <div className="space-y-6" data-testid="pn-form-wrap">
      <div className="space-y-1.5" data-testid="pn-progress-wrap">
        <div className="flex justify-between text-[11px] font-semibold text-[#8A94A2]">
          <span>{sections[sec].title}</span>
          <span data-testid="pn-progress-pct">{Math.round(((sec + 1) / sections.length) * 100)}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#EFF4FC] overflow-hidden">
          <div className="h-full rounded-full bg-[#044CBC] transition-all duration-500" style={{ width: `${((sec + 1) / sections.length) * 100}%` }} data-testid="pn-progress" />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {sections.map((s, i) => (
          <button key={i} onClick={() => setSec(i)} data-testid={`pn-tab-${i}`} className={`shrink-0 px-3.5 py-2 rounded-lg text-xs font-bold transition-colors ${sec === i ? "bg-[#16335F] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
            {s.title}
          </button>
        ))}
      </div>

      <div className="ec-card p-6" data-testid={`pn-section-${sections[sec].testid}`}>
        <h2 className="font-heading font-bold text-slate-900 mb-5">{sections[sec].title}</h2>
        {sections[sec].content}
        <div className="flex justify-between mt-8">
          <button onClick={() => setSec(Math.max(0, sec - 1))} disabled={sec === 0} data-testid="pn-prev" className="px-4 py-2 text-sm font-semibold text-slate-600 disabled:opacity-30">←</button>
          {sec < sections.length - 1 ? (
            <button onClick={() => setSec(sec + 1)} data-testid="pn-next" className="px-6 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C]">→</button>
          ) : (
            <button onClick={submit} disabled={busy} data-testid="pn-submit" className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {busy ? t("common.saving") : t("common.save")}
            </button>
          )}
        </div>
      </div>
        </div>
      )}
    </div>
  );
}
